// TODO: promote this module to a common directory
import { CreditCardIcon } from '@/atoms/Icons/CreditCardIcon'
import { ShopifyBagIcon } from '@/atoms/Icons/ShopifyBagIcon'
import { Image } from '@/atoms/Image'
import { AppleStoreMinimalLogo } from '@/atoms/Logos/AppleStoreMinimalLogo'
import { FireTVLogo } from '@/atoms/Logos/FireTVLogo'
import { RokuLogo } from '@/atoms/Logos/RokuLogo'
import {
  ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_ADD_A_LINE_OFFER_ID,
  ANGEL_GUILD_BASIC_MONTHLY_ADD_A_LINE_OFFER_ID,
} from '@/constants/offer-ids'
import { MembershipSubscription } from '@/services/MembershipService/MembershipService'
import type { GuildPlan, MembershipRenewalPeriod, MembershipTier, SubscriptionStore } from '@/types/codegen-federation'
import { TranslateFunction } from '@/utils/translate/translate-client'
import type { GuildStatus } from '@/utils/users/usersServer'

export const NO_AD_TIERS: GuildPlan[] = ['SILVER', 'GOLD']

export interface GuildTier {
  name: string
  offerId: string
  internalProductId: string
}

/**
 * This has since been renamed "Basic With Ads" in the UI
 */
export const BASIC_TIER = {
  name: 'basic',
  /**
   * The ID taht we use when we buy through Stripe
   */
  offerId: ANGEL_GUILD_BASIC_MONTHLY_ADD_A_LINE_OFFER_ID,
  /**
   * The ID that we use as productId when adding a seat
   */
  internalProductId: 'b56f5418-aff6-4fad-b225-bb622c7f9e88',
}

export const BASIC_NO_ADS_TIER = {
  name: 'basic-no-ads',
  /**
   * The ID taht we use when we buy through Stripe
   */
  offerId: ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_ADD_A_LINE_OFFER_ID,
  /**
   * The ID that we use as productId when adding a seat
   */
  internalProductId: '1dab7d68-af8e-4d0d-a626-60c571c8bf93',
}

export function mapProductToGive(guildStatus: GuildStatus): GuildTier {
  const hasAdFree = Boolean(guildStatus.guildPermissions?.adFree)
  return hasAdFree ? BASIC_NO_ADS_TIER : BASIC_TIER
}

/**
 * Maps the giver's guild plan tier to the gift's tier.
 * Aka "Give what you have".
 * Only interesting part: premium can only give basic.
 *
 * DESIGN NOTE - Note that subscription currently doesn't have its own human-readable
 * name. In the client to date, the membership name is derived from the guild plan.
 * But the subscriptions can now be gifted, which means a sub will belong to a different
 * user than the one who is logged in with a guild plan. For now, however, we
 * can get away with this algorithm because the options of gifted membership are
 * currently directly related to the gifter. There is a potential hole here too,
 * however, because what if the gifter *changes* his subscription between the time
 * of giving and the time of viewing? This will be potentially inaccurate.
 *
 * see https://angelstudiosteam.slack.com/archives/C09P0AN0NNN/p1762285401361199?thread_ts=1762282587.113369&cid=C09P0AN0NNN
 */
export function mapTierIdToGive(guildStatus: GuildStatus): TierId {
  const hasAdFree = Boolean(guildStatus.guildPermissions?.adFree)
  return hasAdFree ? '1basic' : '0basicWithAds'
}

export function formatShortPlanName(t: TranslateFunction, tier: AnyTierId | undefined | null): string | undefined {
  const tierId = normalizeTierId(tier)
  return tierId
    ? {
        '0basicWithAds': t('basicWithAds', 'Basic With Ads'),
        '1basic': t('basic', 'Basic'),
        '2premium': t('premium', 'Premium'),
      }[tierId]
    : undefined
}
export function formatShortPlanNameDefaultBasicWithAds(
  t: TranslateFunction,
  tier: AnyTierId | undefined | null,
): string {
  return formatShortPlanName(t, tier) ?? t('basicWithAds', 'Basic With Ads')
}

export type AnyTierId = TierId | GuildPlan | MembershipTier | string
type TierId = '0basicWithAds' | '1basic' | '2premium'

/**
 * Normalized, because there are multiple systems with multiple ways to
 * identify the tiers, but we only have 3 tiers! Combine all known
 * schemes to one new one, defined here.
 */
export function normalizeTierId(anyTierId: AnyTierId | null | undefined): TierId | undefined {
  const ALL_KNOWN_VALUES: Record<AnyTierId, TierId> = {
    // Guild Plan colors
    BRONZE: '0basicWithAds',
    SILVER: '1basic',
    GOLD: '2premium',

    // Membership Service strings
    basic: '0basicWithAds',
    'basic-no-ads': '1basic',
    premium: '2premium',

    // Membership Service enum
    BASIC: '0basicWithAds',
    BASIC_NO_ADS: '1basic',
    PREMIUM: '2premium',

    // Normalized Tier names
    '0basicWithAds': '0basicWithAds',
    '1basic': '1basic',
    '2premium': '2premium',
  }

  return anyTierId ? ALL_KNOWN_VALUES[anyTierId] : undefined
}

export function filterHighlightedFeatures(t: TranslateFunction, tier: AnyTierId | null | undefined) {
  const tierId = normalizeTierId(tier) ?? '0basicWithAds'

  const selectFeatures: Record<TierId, string[]> = {
    '0basicWithAds': [t('watchMoviesLimitedAds', 'Watch movies and shows with limited ads')],
    '1basic': [t('watchMoviesWithoutAds', 'Watch movies and shows without ads')],
    '2premium': [t('watchMoviesWithoutAds', 'Watch movies and shows without ads')],
  }

  const commonFeatures = [
    t('streamInFullHD', 'Stream in Full HD (1080) quality'),
    t('voteOnConcepts', 'Vote on concepts and films to decide what we green-light next'),
  ]

  return selectFeatures[tierId].concat(commonFeatures)
}

export function formatRenewalPeriod(t: TranslateFunction, period: MembershipRenewalPeriod): string {
  return {
    YEARLY: t('yearly', 'Yearly'),
    MONTHLY: t('monthly', 'Monthly'),
    NEVER_EXPIRES: t('perpetual', 'Perpetual'),
  }[period]
}

export function isSomeMutationUnsupported(subscription: MembershipSubscription): boolean {
  const { cancel, pause } = subscription.supportedOperations
  return !cancel || !pause
}

export function SubscriptionStoreIcon(props: { store: SubscriptionStore }) {
  const STORE_ICON_MAP: Partial<Record<SubscriptionStore, React.ReactNode | undefined>> = {
    AMAZON: <FireTVLogo color="core-gray-950" />,
    ANGEL_COM: <CreditCardIcon size={24} color="core-gray-950" />,
    APP_STORE: <AppleStoreMinimalLogo height={24} color="core-gray-950" />,
    PLAY_STORE: (
      <Image src="/v1741817534/angel-studios/icons/google_play_icon" alt="Google Play" height={24} width={24} />
    ),
    ROKU: <RokuLogo color="roku" height={14} />,
    SHOPIFY: <ShopifyBagIcon size={24} />,
    // TODO: add samsung, vizio
  }

  return STORE_ICON_MAP[props.store]
}
